/**
 * Icon — FlatIcon UIcons web-font wrapper.
 *
 * Bill 2026-05-13 — the seam for phased migration off `lucide-react` to the
 * FlatIcon UIcons family (banker-formal, bank-domain-correct vocabulary).
 * Stylesheets are imported once in `main.tsx`; this primitive emits the
 * canonical `<i class="fi fi-{prefix}-{name}">` markup with size + color
 * controlled inline so callers don't have to know about the underlying
 * font-icon mechanics.
 *
 * Family selection (FlatIcon prefixes):
 *   - `regular` → `fi-rr` (default outline, the everyday look)
 *   - `solid`   → `fi-sr` (filled, for hover/active/emphasis)
 *   - `thin`    → `fi-tr` (1px outline, decorative inline / large display)
 *
 * Naming: pass the icon's slug *without* the `fi-` or family prefix.
 *   <Icon name="user" />                → <i class="fi fi-rr-user" />
 *   <Icon name="user" weight="solid" /> → <i class="fi fi-sr-user" />
 *   <Icon name="user" weight="thin" />  → <i class="fi fi-tr-user" />
 *
 * Browse the full library at https://www.flaticon.com/uicons (the npm
 * package is bundled offline — link is only for slug lookup).
 */
import type { CSSProperties } from 'react';

export type IconWeight = 'regular' | 'solid' | 'thin';

export interface IconProps {
  /** Icon slug — see FlatIcon UIcons catalog. Do NOT include the `fi-` prefix. */
  name: string;
  /** Family variant — defaults to `regular` (outline). */
  weight?: IconWeight;
  /** Pixel size — applied as `font-size`. Defaults to inheriting from parent. */
  size?: number;
  /** Color — applied as `color`. Defaults to inheriting `currentColor`. */
  color?: string;
  /** Optional CSS class for layout (margin, vertical alignment, etc.). */
  className?: string;
  /** Decorative by default. Pass an accessible label to surface to AT. */
  label?: string;
}

const PREFIX_BY_WEIGHT: Record<IconWeight, string> = {
  regular: 'fi-rr',
  solid:   'fi-sr',
  thin:    'fi-tr',
};

export function Icon({
  name,
  weight = 'regular',
  size,
  color,
  className,
  label,
}: IconProps): JSX.Element {
  const cls = `fi ${PREFIX_BY_WEIGHT[weight]}-${name}${className ? ` ${className}` : ''}`;
  const style: CSSProperties = {};
  if (size !== undefined) style.fontSize = size;
  if (color !== undefined) style.color = color;
  // Web-font icons inherit text leading by default — line-height:1 keeps the
  // glyph bounded to the size we set so callers can predict layout.
  style.lineHeight = 1;

  if (label) {
    return <i className={cls} style={style} role="img" aria-label={label} />;
  }
  return <i className={cls} style={style} aria-hidden="true" />;
}
